import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar, type PageId } from "@/components/AppSidebar";
import { lazy, Suspense } from "react";

// Eagerly load the landing page — it's the first thing users see
import { StartHerePage } from "@/pages/StartHerePage";

// Lazily load all other pages to reduce initial bundle size
const VennPage        = lazy(() => import("@/pages/VennPage").then(m => ({ default: m.VennPage })));
const RadarPage       = lazy(() => import("@/pages/RadarPage").then(m => ({ default: m.RadarPage })));
const HypothesisPage  = lazy(() => import("@/pages/HypothesisPage").then(m => ({ default: m.HypothesisPage })));
const HelpPage        = lazy(() => import("@/pages/HelpPage").then(m => ({ default: m.HelpPage })));
const ReferencesPage  = lazy(() => import("@/pages/ReferencesPage").then(m => ({ default: m.ReferencesPage })));
const PitchDeckPage   = lazy(() => import("@/pages/PitchDeckPage").then(m => ({ default: m.PitchDeckPage })));
const AdminPage       = lazy(() => import("@/pages/AdminPage").then(m => ({ default: m.AdminPage })));
import type { AdminTab } from "@/pages/AdminPage";
const ExplainerPage   = lazy(() => import("@/pages/ExplainerPage").then(m => ({ default: m.ExplainerPage })));
const InstagramPage   = lazy(() => import("@/pages/InstagramPage"));
const SettingsPage    = lazy(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
import ErrorBoundary from "./components/ErrorBoundary";
import { PageTransition } from "@/components/PageTransition";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import {
  CircleDot, Hexagon, BookOpen, HelpCircle, Library,
  Play, Pause, MoreHorizontal, BookMarked,
  Presentation, Map, Compass, Camera,
} from "lucide-react";
import SageAssistant from "@/sage";
import { TourProvider } from "@/contexts/TourContext";
import { AppTour } from "@/components/AppTour";
import { TourOfferModal } from "@/components/TourOfferModal";
import { IOSInstallBanner } from "@/components/IOSInstallBanner";
import { ProfileGate } from "@/components/ProfileGate";
import { fx } from "@/lib/effects";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const PAGE_TITLES: Record<PageId, string> = {
  starthere:        "Start Here",
  venn:             "Venn Diagram",
  radar:            "Radar 2D",
  r3d:              "Radar 3D",
  hypothesis:       "Hypothesis",
  help:             "Help",
  references:       "References",
  pitchdeck:        "Pitch Deck",
  admin:            "Admin",
  explainer:        "Reading the Radar",
  instagram:        "Instagram Feed",
  "user-settings":  "Settings",
};

const PAGE_SUBTITLES: Record<PageId, string> = {
  starthere:        "Welcome — understand the thesis, explore the data, and meet Sage",
  venn:             "Visual overview of the three normative dimensions",
  radar:            "Compare profiles across six axes — toggle layers, adjust sliders",
  r3d:              "Drag to rotate · Scroll to zoom · Double-click to reset",
  hypothesis:       "The conceptual framework, data, and argument behind Redefining Normal™",
  help:             "Guide, data sources, FAQs, and glossary",
  references:       "Peer-reviewed citations and data sources",
  pitchdeck:        "Lola & Ber · What If · Hospitality Investment Overview · 2026",
  admin:            "Forecast data, source library, audit log, and app settings",
  explainer:        "A guided walkthrough of how to read the six-axis radar graph",
  instagram:        "Browse the latest @lolaandber posts — filter by timeline, events, or places",
  "user-settings":  "Manage your email preferences and account options",
};

// Primary 5 items always visible in the mobile bottom bar
const MOBILE_NAV_PRIMARY: { id: PageId; label: string; icon: React.ReactNode }[] = [
  { id: "starthere",  label: "Start",      icon: <Compass size={20} />     },
  { id: "venn",       label: "Venn",       icon: <CircleDot size={20} />   },
  { id: "radar",      label: "Radar",      icon: <Hexagon size={20} />     },
  { id: "hypothesis", label: "Hypothesis", icon: <BookOpen size={20} />    },
  { id: "help",       label: "Help",       icon: <HelpCircle size={20} />  },
];

// Additional pages accessible via the "More" bottom sheet
const MOBILE_NAV_MORE: { id: PageId; label: string; icon: React.ReactNode }[] = [
  { id: "explainer",  label: "Reading the Radar",  icon: <Map size={20} />  },
  { id: "instagram",  label: "Instagram Feed",    icon: <Camera size={20} /> },
  { id: "r3d",        label: "Radar 3D",    icon: <Hexagon size={20} />      },
  { id: "references", label: "References",  icon: <Library size={20} />      },
  { id: "pitchdeck",  label: "Pitch Deck",  icon: <Presentation size={20} /> },
  { id: "admin",      label: "Admin",       icon: <BookMarked size={20} />   },
];

function AppLayout({ activePage, setActivePage }: { activePage: PageId; setActivePage: (p: PageId) => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [adminTab, setAdminTab] = useState<AdminTab>("forecasts");
  const [explainerAutoPlay, setExplainerAutoPlay] = useState(false);
  // P1-3: More sheet state
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Per-route error boundary: if a single page crashes, only that page
  // shows the fallback — the sidebar and shell remain functional.
  // Changing pages (activePage) auto-resets the boundary.
  const renderPage = () => {
    const page = (() => {
    switch (activePage) {
      case "starthere":   return <StartHerePage onNavigate={setActivePage} />;
      case "venn":        return <VennPage onNavigate={setActivePage} />;
      case "radar":       return <RadarPage mode="static" />;
      case "r3d":         return <RadarPage mode="3d" />;
      case "hypothesis":  return <HypothesisPage onNavigate={setActivePage} />;
      case "help":        return <HelpPage onNavigate={setActivePage} />;
      case "references":  return <ReferencesPage />;
      case "pitchdeck":   return <PitchDeckPage />;

      case "admin":       return (
        <AdminPage
          activeTab={adminTab}
          setActiveTab={setAdminTab}
        />
      );
      case "instagram":   return <InstagramPage />;
      case "user-settings": return <SettingsPage />;
      case "explainer":   return (
        <ExplainerPage
          isAutoPlaying={explainerAutoPlay}
          setIsAutoPlaying={setExplainerAutoPlay}
          onNavigateToRadar={() => setActivePage("radar")}
          onNavigateTo3D={() => setActivePage("r3d")}
        />
      );
    }
    })();
    return (
      <PageTransition pageKey={activePage}>
        <ErrorBoundary resetKey={activePage}>
          <Suspense fallback={
            <div className="flex flex-1 items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          }>
            {page}
          </Suspense>
        </ErrorBoundary>
      </PageTransition>
    );
  };

  return (
    <>
      {/* P2-3: Skip navigation link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-background focus:text-foreground focus:rounded-md focus:shadow-md focus:border focus:border-border text-sm font-medium"
      >
        Skip to main content
      </a>

      <SidebarProvider defaultOpen={true}>
        {/* On mobile: use dvh minus bottom-nav height (+ safe-area) so content fills exactly the right space */}
        <div className="flex h-[calc(100dvh-64px-env(safe-area-inset-bottom,0px))] md:h-screen overflow-hidden w-full">
          {/* Sidebar — hidden on mobile */}
          <div className="hidden md:block">
            <AppSidebar
              activePage={activePage}
              onNavigate={setActivePage}
              adminTab={adminTab}
              setAdminTab={setAdminTab}
            />
          </div>

          <SidebarInset className="flex flex-col overflow-hidden bg-background flex-1 min-w-0">
            {/* Top header */}
            <header className="flex shrink-0 items-center gap-2 border-b border-border bg-background px-3 md:px-4 lg:px-6 h-14 md:h-[4.5rem]">
              <div className="flex shrink-0 items-center gap-2 md:hidden">
                <img src="https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/lb-logo_6a402776.png" alt="Lola & Ber" width={28} height={28} className="rounded-sm object-contain" />
                <Separator orientation="vertical" className="mx-1 h-5" />
              </div>
              <div className="flex flex-col gap-0 min-w-0 flex-1">
                <h1 className="text-sm md:text-base font-semibold leading-tight text-foreground truncate">
                  {PAGE_TITLES[activePage]}
                </h1>
                {/* P1-2: subtitle now visible on all screen sizes (was hidden sm:block) */}
                <p className="text-xs text-muted-foreground truncate leading-tight">
                  {PAGE_SUBTITLES[activePage]}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-3 shrink-0">
                {activePage === "explainer" ? (
                  <button
                    onClick={() => setExplainerAutoPlay((v) => !v)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold",
                      explainerAutoPlay ? fx.cta : fx.ghost,
                    )}
                    style={{
                      background: explainerAutoPlay ? "var(--primary)" : "var(--muted)",
                      color: explainerAutoPlay ? "var(--primary-foreground)" : "var(--foreground)",
                    }}
                  >
                    {explainerAutoPlay
                      ? <><Pause className="w-3 h-3" /> Pause</>
                      : <><Play  className="w-3 h-3" /> Auto-play</>
                    }
                  </button>
                ) : (
                  <span className="text-xs text-foreground/60 italic hidden lg:block">
                    Lola &amp; Ber&nbsp;&middot;&nbsp;What If&nbsp;&middot;&nbsp;&copy;&nbsp;2026
                  </span>
                )}
              </div>
            </header>

            {/* Page content */}
            <main id="main-content" className="flex-1 overflow-hidden flex flex-col min-h-0">
              {renderPage()}
            </main>
          </SidebarInset>

          {/* Mobile bottom navigation bar */}
          <nav
            className="fixed bottom-0 left-0 right-0 z-50 flex md:hidden border-t border-border bg-background/95 backdrop-blur-sm pb-safe"
            style={{ height: "calc(64px + env(safe-area-inset-bottom, 0px))" }}
            aria-label="Mobile navigation"
          >
            {MOBILE_NAV_PRIMARY.map((item) => {
              const isActive = activePage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  aria-label={item.label}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "relative flex-1 flex flex-col items-center justify-center gap-1",
                    "transition-all duration-150",
                    "active:scale-90",
                    "[&>svg]:transition-transform [&>svg]:duration-200",
                    isActive && "[&>svg]:scale-110",
                  )}
                  style={{ color: isActive ? "var(--primary)" : "var(--muted-foreground)" }}
                >
                  {item.icon}
                  <span className="text-[10px] font-medium leading-none">{item.label}</span>
                  {isActive && (
                    <span className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
            {/* P1-3: Pages button opens a bottom sheet with additional pages */}
            <button
              onClick={() => setMoreSheetOpen(true)}
              aria-label="More pages"
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 relative",
                "transition-all duration-150 active:scale-90",
              )}
              style={{ color: MOBILE_NAV_MORE.some(i => i.id === activePage) ? "var(--primary)" : "var(--muted-foreground)" }}
            >
              <span className="relative">
                <MoreHorizontal size={20} />
                <span
                  className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] rounded-full text-[9px] font-bold flex items-center justify-center px-0.5"
                  style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
                >
                  {MOBILE_NAV_MORE.length}
                </span>
              </span>
              <span className="text-[10px] font-medium leading-none">Pages</span>
            </button>
          </nav>
        </div>
      </SidebarProvider>

      {/* P1-3: Pages bottom sheet */}
      <Sheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe">
          <SheetHeader className="mb-5">
            <SheetTitle className="text-base font-semibold">All Pages</SheetTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Tap any page to navigate</p>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3 pb-8">
            {MOBILE_NAV_MORE.filter(item => item.id !== "admin" || isAdmin).map((item) => {
              const isActive = activePage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActivePage(item.id); setMoreSheetOpen(false); }}
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-xl text-left",
                    isActive ? fx.cta : fx.card,
                    "[&>span:first-child>svg]:transition-transform [&>span:first-child>svg]:duration-200",
                    "hover:[&>span:first-child>svg]:scale-110",
                  )}
                  style={{
                    background: isActive ? "var(--primary)" : "var(--muted)",
                    color: isActive ? "var(--primary-foreground)" : "var(--foreground)",
                    boxShadow: isActive ? "0 2px 8px rgba(71,130,95,0.25)" : undefined,
                  }}
                >
                  <span className="shrink-0 opacity-80">{item.icon}</span>
                  <span className="text-sm font-medium leading-tight">{item.label}</span>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

import { LoginForm } from "@/components/login-form";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import { useTour } from "@/contexts/useTour";
import { useEffect } from "react";

// Registers the navigation callback so TourContext can navigate to the radar page before starting
function TourNavigator({ setActivePage }: { setActivePage: (p: PageId) => void }) {
  const { registerNavigate } = useTour();
  useEffect(() => {
    registerNavigate((page) => setActivePage(page as PageId));
  }, [registerNavigate, setActivePage]);
  return null;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const utils = trpc.useUtils();

  // ── Loading spinner ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background px-6">
        <div className="auth-panel-enter flex flex-col items-center text-center" role="status" aria-live="polite">
          <div className="auth-session-loader relative flex size-14 items-center justify-center rounded-full border border-primary/15 bg-primary/[0.06]">
            <span className="absolute inset-2 rounded-full border border-primary/10" aria-hidden="true" />
            <Loader2 className="size-6 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
          </div>
          <p className="mt-5 text-sm font-medium text-foreground">Preparing your workspace</p>
          <p className="mt-1 text-xs text-muted-foreground">Checking your secure session…</p>
        </div>
      </div>
    );
  }

  // ── Email + password / magic-code login screen ────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
        <LoginForm className="w-full max-w-3xl" onSuccess={() => utils.auth.me.invalidate()} />
      </div>
    );
  }

  return <>{children}</>;
}
function App() {
  const [activePage, setActivePage] = useState<PageId>("starthere");

  // Public routes — accessible without authentication
  const path = window.location.pathname;
  if (path === "/privacy") return <ThemeProvider defaultMode="light"><PrivacyPolicy /></ThemeProvider>;
  if (path === "/terms")   return <ThemeProvider defaultMode="light"><TermsOfService /></ThemeProvider>;

  return (
    <ErrorBoundary>
      <ThemeProvider defaultMode="light">
        <TooltipProvider>
          <Toaster />
          <AuthGate>
            <ProfileGate>
              <TourProvider isAuthenticated={true}>
                <TourNavigator setActivePage={setActivePage} />
                <AppLayout activePage={activePage} setActivePage={setActivePage} />
                <SageAssistant activePage={activePage} pageTitle={PAGE_TITLES[activePage]} />
                <AppTour />
                <TourOfferModal />
                <IOSInstallBanner />
              </TourProvider>
            </ProfileGate>
          </AuthGate>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
